import { SelectedToken } from "../store/SelectedTokensStore";

// Text parsing utilities for selected tokens only
export interface ParsedElement {
  type: "text" | "selectedToken";
  content: string;
  raw: string;
  tokenData?: SelectedToken; // For selected tokens
}

/**
 * Parse content and identify selected tokens only
 */
export const parseContent = (
  content: string,
  selectedTokens: Map<string, SelectedToken>
): ParsedElement[] => {
  const elements: ParsedElement[] = [];
  const patterns = [
    { type: "selectedToken" as const, regex: /@[\w]+\/[\w]+/g },
  ];

  let lastIndex = 0;
  const matches: Array<{
    index: number;
    match: string;
    type: ParsedElement["type"];
  }> = [];

  // Find all matches
  patterns.forEach((pattern) => {
    let match;
    while ((match = pattern.regex.exec(content)) !== null) {
      matches.push({
        index: match.index,
        match: match[0],
        type: pattern.type,
      });
    }
  });

  // Sort by position
  matches.sort((a, b) => a.index - b.index);

  // Build elements
  matches.forEach((match) => {
    // Add text before the match
    if (match.index > lastIndex) {
      elements.push({
        type: "text",
        content: content.slice(lastIndex, match.index),
        raw: content.slice(lastIndex, match.index),
      });
    }

    // Add the styled match
    if (match.type === "selectedToken") {
      // Extract symbol and mode from @symbol/mode
      const tokenKey = match.match.slice(1); // Remove @
      const tokenData = selectedTokens.get(tokenKey);

      // Only style if token is actually selected (exists in store)
      if (tokenData) {
        elements.push({
          type: match.type,
          content: match.match,
          raw: match.match,
          tokenData,
        });
      } else {
        // If not in selected tokens, treat as regular text (no styling)
        elements.push({
          type: "text",
          content: match.match,
          raw: match.match,
        });
      }
    } else {
      elements.push({
        type: match.type,
        content: match.match,
        raw: match.match,
      });
    }

    lastIndex = match.index + match.match.length;
  });

  // Add remaining text
  if (lastIndex < content.length) {
    elements.push({
      type: "text",
      content: content.slice(lastIndex),
      raw: content.slice(lastIndex),
    });
  }

  return elements;
};

/**
 * Intercept message before sending to API
 * Replace @symbol/mode with @id/address[mode] format
 */
export const interceptMessageForAPI = (
  message: string,
  selectedTokens: Map<string, SelectedToken>
): string => {
  let interceptedMessage = message;

  // Find all @symbol/mode patterns
  const tokenPattern = /@([\w]+)\/([\w]+)/g;
  let match;

  while ((match = tokenPattern.exec(message)) !== null) {
    const symbol = match[1];
    const mode = match[2];
    const tokenKey = `${symbol}/${mode}`;
    const tokenData = selectedTokens.get(tokenKey);

    if (tokenData) {
      // Replace @symbol/mode with @id/address[mode]
      const replacement = `@${tokenData.id}[${tokenData.mode}]`;
      interceptedMessage = interceptedMessage.replace(match[0], replacement);
      console.log(`🔄 Intercepted: ${match[0]} -> ${replacement}`);
    }
  }

  return interceptedMessage;
};

/**
 * Convert API format back to display format for editing
 * Replace @id/address[mode] with @symbol/mode
 */
export const convertAPIToDisplay = (
  message: string,
  selectedTokens: Map<string, SelectedToken>
): string => {
  let displayMessage = message;

  // Find all @id/address[mode] patterns
  const apiPattern = /@([\w]+)\[([\w]+)\]/g;
  let match;

  while ((match = apiPattern.exec(message)) !== null) {
    const id = match[1];
    const mode = match[2];

    // Find token by id and mode
    const token = Array.from(selectedTokens.values()).find(
      (t) => t.id === id && t.mode === mode
    );

    if (token) {
      // Replace @id/address[mode] with @symbol/mode
      const replacement = `@${token.symbol}/${token.mode}`;
      displayMessage = displayMessage.replace(match[0], replacement);
      console.log(`🔄 Converted: ${match[0]} -> ${replacement}`);
    }
  }

  return displayMessage;
};
